import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { FaTimesCircle } from 'react-icons/fa'
import { FaX, FaTimes } from 'react-icons/fa6'

const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-7xl',
}

export default function DialogModal({
    isOpen,
    onClose,
    title,
    children,
    showCloseIcon = true,
    size = 'md',
    showBackdrop = true,
    showHeader = true,
    padding = true,
    showDivider = false,
    preventCloseOnBackdropClick = false,
    customOverlayClass = '',
    customPanelClass = '',
}) {
    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog
                as='div'
                className='relative z-50'
                onClose={preventCloseOnBackdropClick ? () => {} : onClose}
            >
                {/* Backdrop with gradient */}
                <Transition.Child
                    as={Fragment}
                    enter='ease-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in duration-200'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                >
                    <div
                        className={`fixed inset-0 transition-all ${
                            showBackdrop
                                ? 'bg-gradient-to-br from-black/40 via-black/30 to-black/20 backdrop-blur-md'
                                : ''
                        } ${customOverlayClass}`}
                    />
                </Transition.Child>

                {/* Glow effect */}
                {showBackdrop && (
                    <div className='fixed inset-0'>
                        <div className='absolute w-64 h-64 rounded-full top-1/4 left-1/4 bg-gradient-to-r from-amber-400/10 to-yellow-400/10 blur-3xl'></div>
                        <div className='absolute rounded-full bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-400/5 to-purple-400/5 blur-3xl'></div>
                    </div>
                )}

                <div className='fixed inset-0 overflow-y-auto'>
                    <div className='flex items-center justify-center min-h-full p-4 sm:p-6'>
                        <Transition.Child
                            as={Fragment}
                            enter='ease-out duration-300'
                            enterFrom='opacity-0 translate-y-4 scale-95'
                            enterTo='opacity-100 translate-y-0 scale-100'
                            leave='ease-in duration-200'
                            leaveFrom='opacity-100 translate-y-0 scale-100'
                            leaveTo='opacity-0 translate-y-4 scale-95'
                        >
                            <Dialog.Panel
                                className={`relative w-full transform overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 text-left align-middle shadow-2xl transition-all duration-300 border border-gray-200/50 ${sizeClasses[size]} ${customPanelClass}`}
                            >
                                {/* Decorative elements */}
                                <div className='absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400'></div>
                                <div className='absolute w-2 h-2 rounded-full opacity-50 top-3 left-3 bg-amber-400'></div>
                                <div className='absolute w-3 h-3 bg-yellow-300 rounded-full bottom-3 right-3 opacity-30'></div>

                                {/* Header */}
                                {showHeader && (
                                    <div
                                        className={`relative ${padding ? 'p-6' : 'pt-6 px-6'} ${
                                            showDivider ? 'border-b border-gray-200 pb-4' : ''
                                        }`}
                                    >
                                        <div className='flex items-start justify-between'>
                                            {title && (
                                                <div className='pr-8'>
                                                    <Dialog.Title className='text-xl font-bold leading-tight text-gray-900'>
                                                        {title}
                                                    </Dialog.Title>
                                                    {/* Optional subtitle can be added here */}
                                                </div>
                                            )}
                                            {showCloseIcon && (
                                                <button
                                                    onClick={onClose}
                                                    className='absolute flex items-center justify-center w-8 h-8 text-gray-400 transition-all duration-200 rounded-full top-6 right-6 hover:text-gray-700 hover:bg-gray-100 group'
                                                    aria-label='Close modal'
                                                >
                                                    <FaTimesCircle className='text-lg transition-transform group-hover:scale-110' />
                                                    <div className='absolute inset-0 transition-all border-2 border-transparent rounded-full group-hover:border-gray-300'></div>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Content */}
                                <div
                                    className={`relative ${padding ? 'p-6' : ''} ${
                                        showHeader && !padding ? 'pt-0 px-6' : ''
                                    }`}
                                >
                                    {children}
                                </div>

                                {/* Optional footer */}
                                {showDivider && (
                                    <div className='absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent'></div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
